import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Plays an audio version of an error message when voice mode is on.
 *
 * Primary path: a **pre-generated branded clip** in Anaka's ElevenLabs voice
 * (`/assets/errors/<scenario>.mp3`, produced by `scripts/generate-error-audio.ts`).
 * If that clip is missing or fails to decode, it transparently falls back to the
 * browser's Web Speech API (the device's system voice) reading `text`, so the
 * feature still works before clips are generated or on unusual browsers.
 *
 * Autoplay policy: browsers may block sound before a user gesture. When the
 * initial play is blocked, `needsGesture` flips true so the UI can offer a
 * "tap to hear" affordance (calling `play` from the tap satisfies the gesture).
 */
export interface ErrorVoice {
  /** Voice playback is possible here (browser; a clip URL or speech support). */
  available: boolean;
  /** Audio (clip or speech) is currently playing. */
  playing: boolean;
  /** Autoplay was blocked — a tap is needed to start. */
  needsGesture: boolean;
  /** Start (or replay) playback. Also the gesture-fallback handler. */
  play: () => void;
  /** Stop playback. */
  stop: () => void;
}

export interface UseErrorVoiceOptions {
  /** URL of the pre-generated branded clip (preferred source). */
  src: string;
  /** Plain-text message — the speech-synthesis fallback. */
  text: string;
  /** Auto-play on mount + expose the control. */
  enabled: boolean;
}

const isBrowser = typeof window !== 'undefined';

export function useErrorVoice({ src, text, enabled }: UseErrorVoiceOptions): ErrorVoice {
  const [playing, setPlaying] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // The branded clip failed (missing / decode error) → use speech instead.
  const clipBrokenRef = useRef(false);
  // Guards auto-play so each distinct clip plays once on mount.
  const playedForRef = useRef<string | null>(null);

  const speechSupported =
    isBrowser &&
    'speechSynthesis' in window &&
    typeof window.SpeechSynthesisUtterance === 'function';

  const stop = useCallback(() => {
    audioRef.current?.pause();
    if (isBrowser) window.speechSynthesis?.cancel();
    setPlaying(false);
  }, []);

  const speak = useCallback(() => {
    if (!speechSupported || !text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onstart = () => {
      setPlaying(true);
      setNeedsGesture(false);
    };
    utterance.onend = () => setPlaying(false);
    utterance.onerror = () => setPlaying(false);
    window.speechSynthesis.speak(utterance);
  }, [speechSupported, text]);

  const play = useCallback(() => {
    if (!isBrowser) return;
    setNeedsGesture(false);

    // Branded clip unavailable → read with the system voice.
    if (clipBrokenRef.current || !src) {
      speak();
      return;
    }

    let audio = audioRef.current;
    if (!audio) {
      audio = new Audio(src);
      audio.preload = 'auto';
      audio.addEventListener('playing', () => {
        setPlaying(true);
        setNeedsGesture(false);
      });
      audio.addEventListener('ended', () => setPlaying(false));
      audio.addEventListener('pause', () => setPlaying(false));
      audio.addEventListener('error', () => {
        clipBrokenRef.current = true;
        setPlaying(false);
        speak(); // missing / undecodable clip → fall back to the system voice
      });
      audioRef.current = audio;
    }

    try {
      audio.currentTime = 0;
    } catch {
      /* not seekable yet */
    }
    const result = audio.play() as Promise<void> | undefined;
    if (result && typeof result.then === 'function') {
      result
        .then(() => setPlaying(true))
        .catch(() => {
          // The `error` handler covers a broken clip; a rejection without one
          // means autoplay was blocked → surface the "tap to hear" control.
          if (!clipBrokenRef.current) setNeedsGesture(true);
        });
    }
  }, [src, speak]);

  // Auto-play once per clip while voice mode is on.
  useEffect(() => {
    if (!enabled || !isBrowser || !src) return;
    if (playedForRef.current === src) return;
    playedForRef.current = src;
    clipBrokenRef.current = false;
    audioRef.current = null;
    setNeedsGesture(false);
    play();
  }, [enabled, src, play]);

  // If voice mode is switched off while a clip or speech is still playing, stop
  // it — the auto-play effect above bails early on `!enabled` without stopping,
  // so playback would otherwise continue after the user leaves voice mode.
  useEffect(() => {
    if (!enabled) stop();
  }, [enabled, stop]);

  // Stop everything when leaving the surface (or the clip changes).
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (isBrowser) window.speechSynthesis?.cancel();
    };
  }, [src]);

  const available = isBrowser && (Boolean(src) || speechSupported);
  return { available, playing, needsGesture, play, stop };
}
